import React from 'react'
import { BRAND } from './FeaturedListings'
import { IdxListings } from './IdxListings'

// ============================================================================
// HomePros — Public /listings page
// ============================================================================
// Page chrome only: the hp-nav header. The listings (header, cards, detail
// sheet, lead form) come from <IdxListings />, which reads the
// get-idx-listings edge function with the ARMLS IDX key.
//
// This page renders <IdxListings /> with NO `source` prop, so every lead
// routes to the broker via the default 'HomePros Hub Listings' source.
// ============================================================================

// ─── LOGO (mirrors OpenHouseLanding Logo, default brokerage "HomePros") ─────
function Logo({ brokerage = 'HomePros' }) {
  return (
    <div className="hp-logo">
      <div className="hp-logo-mark">
        <svg viewBox="0 0 32 32" fill="none" aria-hidden="true">
          <path
            d="M4 16 L16 4 L28 16 L28 28 L20 28 L20 20 L12 20 L12 28 L4 28 Z"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinejoin="round"
          />
          <circle cx="16" cy="16" r="2" fill={BRAND.gold} />
        </svg>
      </div>
      <div className="hp-logo-text">
        <span className="hp-logo-name">{brokerage}</span>
      </div>
    </div>
  )
}

// ─── PAGE ───────────────────────────────────────────────────────────────────
export default function ListingsPage() {
  return (
    <>
      <style>{css}</style>

      <div className="hp-page">
        <header className="hp-nav">
          <div className="hp-nav-inner">
            <Logo brokerage="HomePros" />
            {/* Phone CTA slot — mirrors OpenHouseLanding's conditional render.
                No row-scoped phone source on /listings; left intentionally
                empty until a brand-wide number is plumbed. */}
          </div>
        </header>

        {/* Broker-default routing: no `source` prop → 'HomePros Hub Listings'.
            `frame` gives the listings the page's width and padding; it renders
            only when there are listings, so the fallback keeps its own layout. */}
        <IdxListings
          page="listings"
          variant="page"
          frame={(content) => <main className="hp-listings-main">{content}</main>}
          // Shown when the fetch returns no listings or fails. Replaces the
          // whole listings layout; no lead form.
          emptyFallback={
            <p className="hp-listings-unavailable">
              Our listings aren't available on this page right now. Call (602) 693-0218 or email broker@homeprosarizona.com.
            </p>
          }
        />
      </div>
    </>
  )
}

// ─── STYLES (page shell + nav + logo only) ──────────────────────────────────
// The listings strip's own styles (grid, cards, modal, form, section heading)
// travel with <FeaturedListings />. These shell styles stay here because the
// nav must remain styled even when the strip hides itself on an empty fetch.
const css = `
  :root {
    --hp-navy: ${BRAND.navy};
    --hp-black: ${BRAND.black};
    --hp-white: ${BRAND.white};
    --hp-blue: ${BRAND.blue};
    --hp-gold: ${BRAND.gold};
    --hp-ink: #0a0a0a;
    --hp-ink-soft: #4a4a4a;
    --hp-line: #e8e8e8;
    --hp-bg-soft: #f7f7f5;
  }

  .hp-page {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    color: var(--hp-ink);
    background: var(--hp-white);
    line-height: 1.5;
    -webkit-font-smoothing: antialiased;
    min-height: 100vh;
  }
  .hp-page * { box-sizing: border-box; }

  /* ─── NAV (matches OpenHouseLanding) ─────────────────────────────────── */
  .hp-nav {
    position: sticky; top: 0; z-index: 50;
    background: var(--hp-white);
    border-bottom: 1px solid var(--hp-line);
  }
  .hp-nav-inner {
    max-width: 1200px; margin: 0 auto;
    padding: 16px 24px;
    display: flex; align-items: center; justify-content: space-between;
  }
  .hp-nav-cta {
    font-weight: 700; color: var(--hp-black);
    text-decoration: none; font-size: 15px;
    padding: 10px 18px;
    border: 2px solid var(--hp-black);
    border-radius: 999px;
    transition: all 0.2s;
    min-height: 44px;
    display: inline-flex; align-items: center;
  }
  .hp-nav-cta:hover { background: var(--hp-black); color: var(--hp-white); }

  .hp-logo { display: flex; align-items: center; gap: 10px; color: var(--hp-black); }
  .hp-logo-mark {
    width: 40px; height: 40px;
    display: flex; align-items: center; justify-content: center;
    background: var(--hp-blue); color: var(--hp-white);
    border-radius: 8px;
  }
  .hp-logo-mark svg { width: 24px; height: 24px; }
  .hp-logo-text { display: flex; flex-direction: column; line-height: 1; }
  .hp-logo-name { font-weight: 800; font-size: 18px; letter-spacing: -0.02em; }

  /* No-listings message (2026-10-10). Same size and colour as the listings
     tagline (.hp-l-tagline), centred; lives here because the strip's own
     styles are injected only when there are listings. */
  .hp-listings-unavailable {
    font-size: 16px;
    color: var(--hp-ink-soft);
    text-align: center;
    max-width: 560px;
    margin: 0 auto;
    padding: 56px 24px 96px;
  }

  .hp-listings-main {
    max-width: 1200px;
    margin: 0 auto;
    padding: 40px 24px 72px;
  }

  @media (max-width: 560px) {
    .hp-nav-inner { padding: 12px 16px; }
    .hp-listings-main { padding: 28px 16px 56px; }
    .hp-listings-unavailable { padding: 32px 16px 56px; }
  }
`
